import React from "react";

const DEFAULT_DEBOUNCE_MS = 300;

/** One request for a page of remote options. */
export interface RemoteOptionsRequest {
  readonly query: string;
  readonly cursor?: string;
  readonly signal: AbortSignal;
}

/** A page of remote options; a `null` cursor means the last page. */
export interface RemoteOptionsPage<T> {
  readonly options: readonly T[];
  readonly nextCursor: string | null;
}

export interface UseRemoteOptionsInput<T> {
  readonly load: (
    request: RemoteOptionsRequest,
  ) => Promise<RemoteOptionsPage<T>>;
  /** The search text. Trimmed before it is sent. */
  readonly query: string;
  /** Debounce for a changed query (default 300ms). */
  readonly debounceMs?: number | undefined;
  /** Send nothing until the trimmed query has this many characters. */
  readonly minQueryLength?: number | undefined;
  /** `false` sends nothing and aborts what is in flight. Default `true`. */
  readonly enabled?: boolean | undefined;
}

export interface RemoteOptionsState<T> {
  readonly options: readonly T[];
  readonly nextCursor: string | null;
  readonly loading: boolean;
  readonly loadingMore: boolean;
  readonly failed: boolean;
  readonly loadMoreFailed: boolean;
  /** Characters still needed before a search is sent (0 when none). */
  readonly remaining: number;
  readonly loadNextPage: () => void;
  readonly retry: () => void;
}

/**
 * Request state for a remote, cursor-paged option list (FilterOptionList,
 * Combobox `loadOptions`). Caching stays in the caller's loader; this hook owns
 * request timing, cancellation, retry and cursor pagination. A request loads
 * at once when the list is switched on or retried, and a changed query waits
 * for the debounce. A stale reply never replaces a newer one: every request
 * carries an AbortSignal and a sequence number, and either one retires it.
 */
export function useRemoteOptions<T extends { readonly value: string }>({
  load,
  query,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minQueryLength = 0,
  enabled = true,
}: UseRemoteOptionsInput<T>): RemoteOptionsState<T> {
  const normalizedQuery = query.trim();
  const remaining = Math.max(0, minQueryLength - normalizedQuery.length);
  const active = enabled && remaining === 0;
  const [options, setOptions] = React.useState<readonly T[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(active);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = React.useState(false);
  const [retryToken, setRetryToken] = React.useState(0);
  const sequence = React.useRef(0);
  const loadImmediately = React.useRef(true);
  const loadRef = React.useRef(load);
  const loadMoreController = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    loadRef.current = load;
  }, [load]);

  /* A new search drops the previous query's cursor on the render that starts
     it, not when its debounced request fires: until then "Load more" would
     still carry the OLD cursor and merge that query's page 2 into this one.
     Switching the list on shows the loading row at once, never a moment of
     "no matches" before the first request is sent. */
  const [requestKey, setRequestKey] = React.useState({
    query: normalizedQuery,
    active,
  });
  if (requestKey.query !== normalizedQuery || requestKey.active !== active) {
    setRequestKey({ query: normalizedQuery, active });
    setNextCursor(null);
    setLoadingMore(false);
    setLoadMoreFailed(false);
    if (active && !requestKey.active) {
      setLoading(true);
      setFailed(false);
    } else if (active && requestKey.query !== normalizedQuery) {
      /* The loaded options answer the OLD query: drop them and show the
         loading row until the debounced request answers, so Enter cannot
         pick a result for text the user has already replaced. */
      setOptions([]);
      setLoading(true);
      setFailed(false);
    }
  }

  React.useEffect(() => {
    if (!enabled) {
      loadImmediately.current = true;
      return;
    }
    if (remaining > 0) {
      loadImmediately.current = false;
      return;
    }
    const controller = new AbortController();
    const seq = ++sequence.current;
    const delay = loadImmediately.current ? 0 : debounceMs;
    loadImmediately.current = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setFailed(false);
      void loadRef
        .current({ query: normalizedQuery, signal: controller.signal })
        .then((page) => {
          if (controller.signal.aborted || seq !== sequence.current) return;
          setOptions(page.options);
          setNextCursor(page.nextCursor);
        })
        .catch(() => {
          if (controller.signal.aborted || seq !== sequence.current) return;
          setOptions([]);
          setNextCursor(null);
          setFailed(true);
        })
        .finally(() => {
          if (controller.signal.aborted || seq !== sequence.current) return;
          setLoading(false);
        });
    }, delay);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
      loadMoreController.current?.abort();
      loadMoreController.current = null;
    };
  }, [debounceMs, enabled, normalizedQuery, remaining, retryToken]);

  const loadNextPage = (): void => {
    if (!nextCursor || loadingMore) return;
    const controller = new AbortController();
    loadMoreController.current = controller;
    const seq = sequence.current;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    void loadRef
      .current({
        query: normalizedQuery,
        cursor: nextCursor,
        signal: controller.signal,
      })
      .then((page) => {
        if (controller.signal.aborted || seq !== sequence.current) return;
        setOptions((current) => {
          const merged = new Map(
            current.map((option) => [option.value, option]),
          );
          for (const option of page.options) merged.set(option.value, option);
          return [...merged.values()];
        });
        setNextCursor(page.nextCursor);
      })
      .catch(() => {
        if (controller.signal.aborted || seq !== sequence.current) return;
        setLoadMoreFailed(true);
      })
      .finally(() => {
        if (loadMoreController.current === controller) {
          loadMoreController.current = null;
        }
        if (controller.signal.aborted || seq !== sequence.current) return;
        setLoadingMore(false);
      });
  };

  return {
    options,
    nextCursor,
    loading,
    loadingMore,
    failed,
    loadMoreFailed,
    remaining,
    loadNextPage,
    retry: () => {
      loadImmediately.current = true;
      setLoading(true);
      setFailed(false);
      setRetryToken((token) => token + 1);
    },
  };
}
